import { useState } from 'react';
import { BookmarkIcon, BotIcon, DatabaseIcon, FileKeyIcon, FileTextIcon, HashIcon, Link2Icon, MessageSquareIcon, PlusIcon, RouteIcon, SparklesIcon, UserIcon, WandSparklesIcon } from 'lucide-react';
import { activityFor, attentionCountFor, chatsFor, linkedFor, statePlayFor, type ActivityEntry, type ActivityTag, type LinkedCategory } from '../../data/trackDetail';
import { avatarHue, type Hue } from '../alt/hues';
import { EmptyState, Glyph, MetaStat, Panel, Row, RowText, SearchField, SelectPill } from '../dashboard/ui';
export type TrackDetailViewProps = {
  trackName: string;
  group: string;
  groupHue: Hue;
  exportMode?: boolean;
};
const TAG_DOT: Record<ActivityTag, string> = {
  Discussion: 'var(--app-info)',
  Decision: 'var(--app-success)',
  Issue: 'var(--app-danger)',
  Progress: '#6D4CBF',
  'Customer signal': 'var(--app-warning)',
  Metric: '#4159A6'
};
const CATEGORY_ICON: Record<string, typeof BookmarkIcon> = {
  bookmarks: BookmarkIcon,
  prompts: WandSparklesIcon,
  skills: SparklesIcon,
  credentials: FileKeyIcon,
  databases: DatabaseIcon,
  slack: HashIcon,
  chats: MessageSquareIcon,
  context: FileTextIcon
};
export function TrackOverviewView({
  trackName,
  group,
  groupHue,
  exportMode = false
}: TrackDetailViewProps) {
  const activity = activityFor(trackName);
  const recent = activity.flatMap(day => day.entries).slice(0, 5);
  const chats = chatsFor(trackName);
  const linked = linkedFor(trackName);
  const allLinked = [...linked.left, ...linked.right];
  const context = allLinked.find(category => category.id === 'context')?.items ?? [];
  const primaryChannel = allLinked.find(category => category.id === 'slack')?.items[0];
  if (exportMode) {
    return <TrackOverviewExport trackName={trackName} group={group} groupHue={groupHue} recent={recent} data-id="element-3996" data-mp="32rztgm62ti4h" />;
  }
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-x-8 gap-y-8 xl:grid-cols-12" data-id="element-3997" data-mp="2xtoig841p1ko">
      <section className="xl:col-span-7" data-id="element-3998" data-mp="34wv0uwv7fht1">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-3999" data-mp="34y67d4g1b3nu">
          <h2 className="text-[17px] font-semibold text-app" data-id="element-4000" data-mp="39w7pnn5myyy9">State of play</h2>
          <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold" style={{
          backgroundColor: groupHue.tint,
          color: groupHue.ink
        }} data-id="element-4001" data-mp="3eu9763neyq7m">
            <span className="size-1.5 rounded-full" style={{
            backgroundColor: groupHue.solid
          }} data-id="element-4002" data-mp="3okc9iugfdgim" />
            {group}
          </span>
        </div>
        <p className="mt-2.5 max-w-[66ch] text-[15px] leading-relaxed text-app-muted" data-id="element-4003" data-mp="3skmdk1knpwuu">{statePlayFor(trackName)}</p>
        <dl className="mt-5 grid grid-cols-3 gap-4 border-t border-app-divider pt-4" data-id="element-4004" data-mp="1gfm94088pbp">
          <MetaStat label="Active chats" value={chats.length} data-id="element-4005" data-mp="6debp98y483k" />
          <MetaStat label="Recent events" value={recent.length} data-id="element-4006" data-mp="237ixnvhxy7xb" />
          <MetaStat label="Needs review" value={attentionCountFor(trackName)} data-id="element-4007" data-mp="239c6nhg1148h" />
        </dl>

        <h3 className="mb-2.5 mt-7 text-[13px] font-semibold text-app-muted" data-id="element-4008" data-mp="2886ooh666orj">Observations</h3>
        <Panel data-id="element-4009" data-mp="20nw50loh27lt">
          {recent.map((entry, index) => <Row key={`${entry.time}-${index}`} data-id="element-4010" data-mp="1vvba1gttxogp" data-mpi={index}>
              <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-4011" data-mp="1vujn0ueasz21" />
              <RowText title={entry.text} meta={entry.time} data-id="element-4012" data-mp="1qxjjaq4x88e3" />
              <TagLegend tag={entry.tags[0]} data-id="element-4013" data-mp="1lxx78k0twy6v" />
            </Row>)}
        </Panel>
      </section>

      <section className="xl:col-span-5" data-id="element-4014" data-mp="2jl5mvye5fd0y">
        <h3 className="mb-2.5 text-[13px] font-semibold text-app-muted" data-id="element-4015" data-mp="2jk4clw4yiuka">Chats</h3>
        <Panel data-id="element-4016" data-mp="2em2ogwsoc51y">
          {chats.map(chat => <Row key={chat.ref} data-id="element-4017" data-mp="2en41h6t931mb">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{
            backgroundColor: avatarHue(chat.initials)
          }} data-id="element-4018" data-mp="29mzlrj062ax5">{chat.initials}</span>
              <RowText title={chat.name} meta={chat.when} data-id="element-4019" data-mp="y7uj2f01ccdj" />
              <span className="shrink-0 font-mono text-[11px] text-app-subtle" data-id="element-4020" data-mp="y9dyy4hs4les">{chat.ref}</span>
            </Row>)}
        </Panel>

        <h3 className="mb-2.5 mt-7 text-[13px] font-semibold text-app-muted" data-id="element-4021" data-mp="2i371kbmmvcuc">Linked</h3>
        <Panel data-id="element-4022" data-mp="2i1cccl4bwykg">
          {context.map(item => <Row key={item.title} data-id="element-4023" data-mp="2mu5hha3pm8x2">
              <Glyph icon={FileTextIcon} data-id="element-4024" data-mp="2mv895k8uwfe1" />
              <RowText title={item.title} meta={item.meta} data-id="element-4025" data-mp="2859evskf9o3u" />
            </Row>)}
          {primaryChannel && <Row data-id="element-4026" data-mp="2d3uepqvri5rr">
              <Glyph icon={HashIcon} data-id="element-4027" data-mp="2d3ax6ba0xje9" />
              <RowText title={primaryChannel.title} meta={primaryChannel.meta} data-id="element-4028" data-mp="2d4dmwfvm1fsl" />
            </Row>}
        </Panel>
      </section>
    </div>;
}
function TrackOverviewExport({
  trackName,
  group,
  groupHue,
  recent
}: {
  trackName: string;
  group: string;
  groupHue: Hue;
  recent: ActivityEntry[];
}) {
  const chats = chatsFor(trackName).slice(0, 2);
  const linked = linkedFor(trackName);
  const allLinked = [...linked.left, ...linked.right];
  const context = allLinked.find(category => category.id === 'context')?.items.slice(0, 2) ?? [];
  const channel = allLinked.find(category => category.id === 'slack')?.items[0];
  const priority = recent.find(entry => entry.tags.includes('Issue')) ?? recent[0];
  const observations = [priority, ...recent.filter(entry => entry !== priority)].filter(Boolean).slice(0, 3) as ActivityEntry[];
  return <div className="mx-auto w-full max-w-[1080px]" data-id="element-4029" data-mp="bj045fgstwal">
      {/* Name the product concept before showing its contents. The route mark,
       feature label, and outcome title make this identifiable as a Track even
       when the exported frame is viewed without surrounding marketing copy. */}
      <header className="flex items-start justify-between gap-8 border-b border-app-divider pb-4" data-id="element-4030" data-mp="3axvep6h4sq6f">
        <div className="flex min-w-0 items-start gap-3" data-id="element-4031" data-mp="3oorutxtvfewl">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-app-text text-white" data-id="element-4032" data-mp="3oqb9xl3sjjws">
            <RouteIcon size={17} strokeWidth={1.9} data-id="element-4033" data-mp="3jwgrekzx9aza" />
          </span>
          <div className="min-w-0" data-id="element-4034" data-mp="2w760dv8diuyc">
            <p className="text-[11px] font-semibold text-app-subtle" data-id="element-4035" data-mp="2s73v7c3fjjeb">Track overview</p>
            <h2 className="mt-1 truncate text-[22px] font-semibold tracking-[-0.025em] text-app" data-id="element-4036" data-mp="2s5az2qqg0hkk">{trackName}</h2>
            <p className="mt-1 text-[11.5px] text-app-muted" data-id="element-4037" data-mp="105kpm7au0rqm">One place for the context, conversations, and ongoing work behind this outcome.</p>
          </div>
        </div>
        <span className="mt-1 inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold" style={{
        backgroundColor: groupHue.tint,
        color: groupHue.ink
      }} data-id="element-4038" data-mp="14ydviyi1e64a">
          <span className="size-1.5 rounded-full" style={{
          backgroundColor: groupHue.solid
        }} data-id="element-4039" data-mp="c96mv6ovc05b" />
          {group}
        </span>
      </header>

      {/* One dominant result gives the eye somewhere to land; the prose and
       secondary metrics explain it without creating another row of cards. */}
      <section className="grid grid-cols-12 gap-7 py-5" data-id="element-4040" data-mp="r6h5r13uc8b2">
        <div className="col-span-8" data-id="element-4041" data-mp="1xlhtijippk6k">
          <h3 className="text-[12px] font-semibold text-app-muted" data-id="element-4042" data-mp="1xkgfq7abajl5">State of play</h3>
          <p className="mt-2 max-w-[62ch] text-[13px] leading-relaxed text-app-muted" data-id="element-4043" data-mp="22iqd8ojidw9x">{statePlayFor(trackName)}</p>
        </div>
        <div className="col-span-4 border-l border-app-divider pl-6" data-id="element-4044" data-mp="3n46l3v9q0m6t">
          <p className="text-[32px] font-semibold leading-none tracking-[-0.04em] text-app" data-id="element-4045" data-mp="3i4buztxrs0mt"><span className="text-[var(--app-accent)]" data-id="element-4046" data-mp="3dbinwzetwg7k">14</span><span className="text-[19px] text-app-subtle" data-id="element-4047" data-mp="3da7lb2x4dei1"> / 18</span></p>
          <p className="mt-2 text-[12px] font-semibold text-app" data-id="element-4048" data-mp="38cedy6k2yeg6">Leads routed automatically</p>
          <p className="mt-1 text-[10.5px] text-app-subtle" data-id="element-4049" data-mp="33ew4mf25hx7n">4 need review</p>
        </div>
      </section>

      <div className="grid grid-cols-12 gap-8 border-t border-app-divider pt-5" data-id="element-4050" data-mp="2zay6o7okasg6">
        <section className="col-span-8" data-id="element-4051" data-mp="2uehgl7xsy40g">
          <h3 className="mb-2.5 text-[12px] font-semibold text-app-muted" data-id="element-4052" data-mp="2ufixht1i1kq3">Observations</h3>
          <div className="overflow-hidden rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4053" data-mp="2pgg08urrgiso">
            {observations.map((entry, index) => {
            const isPriority = entry === priority;
            return <div key={`${entry.time}-${index}`} className={`flex items-center gap-3 px-4 py-3 ${index > 0 ? 'border-t border-app-divider' : ''} ${isPriority ? 'bg-app-subtle' : ''}`} data-id="element-4054" data-mp="3sn0hi7u13zkv" data-mpi={index}>
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-app-canvas" data-id="element-4055" data-mp="1ijyni8woctx">
                    {isPriority ? <span className="size-1.5 rounded-full" style={{
                  backgroundColor: TAG_DOT.Issue
                }} data-id="element-4056" data-mp="3nvs83bsp8e7p" /> : entry.author === 'bot' ? <BotIcon size={13} className="text-app-subtle" data-id="element-4057" data-mp="39w9bng992rb8" /> : <UserIcon size={13} className="text-app-subtle" data-id="element-4058" data-mp="39yltzuix73ot" />}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-4059" data-mp="3etixyn1cemuu">
                    <p className={`truncate text-[12px] text-app ${isPriority ? 'font-semibold' : 'font-medium'}`} data-id="element-4060" data-mp="2zz4phgfemvuf">{entry.text}</p>
                    <p className="mt-0.5 text-[10px] text-app-subtle" data-id="element-4061" data-mp="34xy0n277yarf">{entry.time}{isPriority ? ' · needs a person' : ''}</p>
                  </div>
                  <TagLegend tag={entry.tags[0]} data-id="element-4062" data-mp="135t1p6rayd77" />
                </div>;
          })}
          </div>
        </section>

        {/* Chats and Linked are retained as provenance, but share one quiet
         column and no extra card chrome. */}
        <aside className="col-span-4 border-l border-app-divider pl-6" data-id="element-4063" data-mp="2iqbrs20f2b34">
          <h3 className="mb-2.5 text-[12px] font-semibold text-app-muted" data-id="element-4064" data-mp="2xe6odthjozlh">Chats</h3>
          <div className="space-y-2.5" data-id="element-4065" data-mp="2sngimf57iuq9">
            {chats.map(chat => <p key={chat.ref} className="truncate text-[11.5px] text-app-muted" data-id="element-4066" data-mp="2slvourqpvftg"><span className="font-medium text-app" data-id="element-4067" data-mp="36dk0ktx2029w">{chat.name}</span> · {chat.when}</p>)}
          </div>

          <h3 className="mb-2.5 mt-5 text-[12px] font-semibold text-app-muted" data-id="element-4068" data-mp="32c85w9zboquu">Linked</h3>
          <div className="space-y-2.5" data-id="element-4069" data-mp="3gbpzd2nkcsaj">
            {context.map(item => <p key={item.title} className="truncate text-[11.5px] text-app-muted" data-id="element-4070" data-mp="3bdogoiu1uuzl"><span className="font-medium text-app" data-id="element-4071" data-mp="3behe2t74c852">{item.title}</span> · {item.meta}</p>)}
            {channel && <p className="truncate text-[11.5px] text-app-muted" data-id="element-4072" data-mp="1lz02qd5t94zg"><span className="font-medium text-app" data-id="element-4073" data-mp="1h0ghyg2ydz8o">{channel.title}</span> · {channel.meta}</p>}
          </div>
        </aside>
      </div>
    </div>;
}
export function TrackLinkedView({
  trackName
}: TrackDetailViewProps) {
  const inventory = linkedFor(trackName);
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-4074" data-mp="12b0n35zv1v7g">
      <Panel data-id="element-4075" data-mp="xbedr7n5kz3x">{inventory.left.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-4076" data-mp="t8rgusbmknzv" />)}</Panel>
      <Panel data-id="element-4077" data-mp="1py8srp17b6a0">{inventory.right.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-4078" data-mp="1px77krqh13ek" />)}</Panel>
    </div>;
}
export function TrackActivityView({
  trackName
}: TrackDetailViewProps) {
  const days = activityFor(trackName);
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'All types' | ActivityTag>('All types');
  const [authors, setAuthors] = useState<Set<ActivityEntry['author']>>(() => new Set(['human', 'bot']));
  const q = query.trim().toLowerCase();
  const toggleAuthor = (author: ActivityEntry['author']) => setAuthors(current => {
    const next = new Set(current);
    if (next.has(author) && next.size > 1) next.delete(author);else next.add(author);
    return next;
  });
  const matches = (entry: ActivityEntry) => authors.has(entry.author) && (type === 'All types' || entry.tags.includes(type)) && (!q || entry.text.toLowerCase().includes(q));
  return <div className="mx-auto w-full max-w-[1040px]" data-id="element-4079" data-mp="4fobj4et0yr4">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" data-id="element-4080" data-mp="4ggamp7m1em5">
        <div className="w-full sm:w-[260px]" data-id="element-4081" data-mp="9dgfwxwmydc7">
          <SearchField value={query} onChange={setQuery} placeholder="Search this track" label="Search activity" data-id="element-4082" data-mp="edcicspopwpn" />
        </div>
        <div className="flex items-center gap-2" data-id="element-4083" data-mp="j3svscti9ic0">
          <SelectPill value={type} onChange={value => setType(value as typeof type)} options={['All types', 'Discussion', 'Decision', 'Issue', 'Progress', 'Customer signal', 'Metric']} label="Filter by event type" data-id="element-4084" data-mp="j65nhhp2f729" />
          <div className="inline-flex h-9 items-center gap-0.5 rounded-lg bg-app-subtle p-1" data-id="element-4085" data-mp="x3ty7wpf4xtl">
            {([['human', 'Human', UserIcon], ['bot', 'Bot', BotIcon]] as const).map(([key, label, Icon]) => <button key={key} type="button" onClick={() => toggleAuthor(key)} aria-pressed={authors.has(key)} className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-medium transition-colors duration-200 ease-out ${authors.has(key) ? 'bg-app-surface text-app shadow-[0_1px_2px_rgba(20,20,24,0.08)]' : 'text-app-subtle hover:text-app'}`} data-id="element-4086" data-mp="2t4m6wwyy4zpi">
                <Icon size={12} data-id="element-4087" data-mp="3bvvhzaod7wj7" />
                {label}
              </button>)}
          </div>
        </div>
      </div>

      {days.map(day => {
      const entries = day.entries.filter(matches);
      if (!entries.length) return null;
      return <div key={day.date} className="mb-7 last:mb-0" data-id="element-4088" data-mp="3gwt05n9dkqkp">
            <h3 className="mb-2.5 text-[13px] font-semibold text-app-muted" data-id="element-4089" data-mp="3gxchp2v45cy7">{day.date}</h3>
            <Panel data-id="element-4090" data-mp="1ri4yafyeur0x">
              {entries.map((entry, index) => <Row key={`${day.date}-${entry.time}-${index}`} data-id="element-4091" data-mp="1mis234lng7lo" data-mpi={index}>
                  <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-4092" data-mp="1mkmpqqob2dtg" />
                  <span className="shrink-0 font-mono text-[12px] text-app-subtle" data-id="element-4093" data-mp="20zpp2kg0e65">{entry.time}</span>
                  <RowText title={entry.text} meta={entry.author === 'bot' ? 'Giga' : 'Team member'} data-id="element-4094" data-mp="6z195ols6fi5" />
                  <span className="hidden shrink-0 items-center gap-2.5 sm:flex" data-id="element-4095" data-mp="3ofshlnda081f">
                    {entry.tags.map(tag => <TagLegend key={tag} tag={tag} data-id="element-4096" data-mp="3tdc9vr6t6lcd" />)}
                  </span>
                </Row>)}
            </Panel>
          </div>;
    })}
      {days.every(day => !day.entries.some(matches)) && <EmptyState data-id="element-4097" data-mp="3kg1sdo3edzz0">Nothing here matches those filters.</EmptyState>}
    </div>;
}
function LinkedCategoryRows({
  category
}: {
  category: LinkedCategory;
}) {
  const Icon = CATEGORY_ICON[category.id] ?? Link2Icon;
  return <>
      <div className="flex items-center justify-between border-b border-app-divider px-4 py-2.5" data-id="element-4098" data-mp="3ai4799gu4jam">
        <p className="flex items-center gap-2 text-[12.5px] font-semibold text-app-muted" data-id="element-4099" data-mp="13o0ht4qa1jav">
          <Icon size={13} strokeWidth={1.9} className="text-app-subtle" data-id="element-4100" data-mp="18mtskpe6iw7c" />
          {category.name} · {category.items.length}
        </p>
        <button type="button" aria-label={`Add ${category.name}`} className="flex size-6 items-center justify-center rounded-md text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4101" data-mp="30pxxry7y7wzr">
          <PlusIcon size={13} data-id="element-4102" data-mp="2qr8eq24bg6hx" />
        </button>
      </div>
      {category.items.length ? category.items.map((item, index) => <Row key={`${item.title}-${index}`} data-id="element-4103" data-mp="2lurrrb8ury6f" data-mpi={index}>
            <RowText title={item.title} meta={item.meta} data-id="element-4104" data-mp="2h2qfhpboq7ge" />
            {item.badges?.map(badge => <span key={badge} className="shrink-0 rounded-md bg-app-subtle px-2 py-0.5 text-[11px] font-medium text-app-muted" data-id="element-4105" data-mp="2d0wjvvgcjtn3">{badge}</span>)}
          </Row>) : <p className="border-b border-app-divider px-4 py-3.5 text-[13px] text-app-subtle last:border-0" data-id="element-4106" data-mp="2833bcvrkmnjn">No {category.name.toLowerCase()} linked yet.</p>}
    </>;
}
function TagLegend({
  tag
}: {
  tag?: ActivityTag;
}) {
  if (!tag) return null;
  return <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-app-muted" data-id="element-4107" data-mp="1nzperlmwqh7e">
      <span className="size-1.5 rounded-full" style={{
      backgroundColor: TAG_DOT[tag]
    }} data-id="element-4108" data-mp="1sysb8hotnf3r" />
      {tag}
    </span>;
}